<template>
  <form class="vl-form" @submit.prevent="confirm()">
    <vl-grid v-if="definition" v-vl-spacer:bottom.medium>
      <vl-column width="12" width-s="12" width-xs="12">
        <vl-properties-list v-if="definition">
          <vl-properties-label>{{ $t('productprocessdefinition.detail.id') }}</vl-properties-label>
          <vl-properties-data>{{ definition.id }}</vl-properties-data>
          <vl-properties-label>{{ $t('productprocessdefinition.detail.label') }}</vl-properties-label>
          <vl-properties-data>{{ definition.label }}</vl-properties-data>
          <vl-properties-label>{{ $t('productprocessdefinition.detail.organisation') }}</vl-properties-label>
          <vl-properties-data>{{ definition.organisation }} ({{ definition.ovoNumber }})</vl-properties-data>
        </vl-properties-list>
      </vl-column>
      <vl-column width="12" width-s="12" width-xs="12">
        <vl-form-message-label for="statusId">{{
          $t('productprocessdefinition.list.filter.status.label')
        }}</vl-form-message-label>
        <vl-select
          id="statusId"
          v-model="data.statusType"
          name="statusId"
          mod-block
          :mod-disabled="isSaving"
          :placeholder-text="$t('vl-select.placeholder')"
        >
          <option v-for="statusType in statusTypes" :key="statusType" :value="statusType">
            {{ statusType }}
          </option>
        </vl-select>
      </vl-column>
      <vl-column width="12" width-s="12" width-xs="12">
        <vl-action-group v-vl-spacer:top.medium>
          <vl-button :mod-disabled="isSaving" :mod-loading="isSaving">{{ $t('general.save') }}</vl-button>
          <vl-button :mod-disabled="isSaving" mod-icon-before mod-secondary tag-name="a" @click.prevent="cancel">
            {{ $t('general.cancel') }}
          </vl-button>
        </vl-action-group>
      </vl-column>
    </vl-grid>
    <template v-if="isLoading">
      <vl-region>
        <div v-vl-align:center>
          <vl-loader :message="$t('general.formLoading')" />
        </div>
      </vl-region>
    </template>
    <template v-if="error">
      <vl-region>
        <vl-alert icon="warning" :title="$t('general.formLoadingError')" :content="error" mod-naked mod-error />
      </vl-region>
    </template>
  </form>
</template>
<script lang="ts" setup>
import { computed, onMounted } from 'vue';
import { useProductProcessDefinitionDetailStore } from '@/modules/configuration/store/product-process-definition/product-process-definition-detail.store';
import { useProductProcessDefinitionListStore } from '@/modules/configuration/store/product-process-definition/product-process-definition-list.store';
import { reactive } from 'vue';
import { ENUM_StatusTypes } from '@/modules/configuration/common';
import { useModalStore } from '@/modules/core/store/modal.store';
import { parseException } from '@/modules/core/store/alert.store';

const detailStore = useProductProcessDefinitionDetailStore();
const listStore = useProductProcessDefinitionListStore();

const props = defineProps({
  id: {
    type: Number,
    required: true
  }
});

const data = reactive({
  definition: null,
  statusType: null,
  isSaving: false,
  isLoading: false,
  error: null
});

const statusTypes = computed((): string[] => {
  return Object.values(ENUM_StatusTypes).map((c) => c.name);
});

const getStatusTypeById = (id: number) => {
  return Object.values(ENUM_StatusTypes).find((c) => c.id === id);
};

onMounted(async () => {
  try {
    data.isLoading = true;
    await loadProductProcessDefinition();
  } catch (error) {
    data.error = parseException(error);
  } finally {
    data.isLoading = false;
  }
});

const loadProductProcessDefinition = async () => {
  const productProcessDefinition = await detailStore.getDetail(props.id);
  if (productProcessDefinition) {
    data.definition = productProcessDefinition;
    data.statusType = getStatusTypeById(productProcessDefinition.statusId)?.name;
  }
};

const definition = computed(() => {
  return data.definition;
});

const isLoading = computed(() => {
  return data.isLoading;
});

const isSaving = computed(() => {
  return data.isSaving;
});

const error = computed(() => {
  return data.error;
});

const confirm = async () => {
  const isSuccess = await changeStatus();
  if (isSuccess) {
    updateSearchResults();
  }
  closeModal();
};

const updateSearchResults = () => {
  const item = listStore.getDefinition(props.id);
  if (item) {
    listStore.updateItem({
      ...item,
      statusId: Object.values(ENUM_StatusTypes).find((c) => c.name === data.statusType)?.id
    });
  }
};

const changeStatus = async (): Promise<boolean> => {
  let isSuccess = false;
  try {
    data.isSaving = true;
    isSuccess = await detailStore.changeStatus(props.id, data.statusType);
  } catch (e) {
    console.error(e);
  } finally {
    data.isSaving = false;
  }
  return isSuccess;
};

const cancel = () => {
  closeModal();
};

const closeModal = () => {
  useModalStore().hideModal();
};
</script>
<style lang="scss" scoped></style>
